/**
 * Cache service for storing API responses
 *
 * Provides a centralized caching mechanism to avoid redundant API calls
 * and improve performance across the application.
 */
class CacheService {
  constructor() {
    // Main cache storage
    this.cache = {}

    // Default cache time-to-live in milliseconds (30 minutes)
    this.defaultTTL = 30 * 60 * 1000

    // Maximum cache size (number of entries)
    this.maxSize = 100

    // Cache entry tracking for LRU implementation
    this.accessOrder = []
  }

  /**
   * Generate a cache key from parameters
   *
   * @param {string} endpoint API endpoint
   * @param {Object} params Request parameters
   * @returns {string} Cache key
   */
  generateKey(endpoint, params) {
    // Convert params object to sorted string for consistent key generation
    const sortedParams = params
      ? Object.keys(params)
          .sort()
          .map((key) => {
            // Check if the value is an array
            if (Array.isArray(params[key])) {
              // Sort the array and join by commas
              return `${key}=${params[key].sort().join(',')}`
            } else {
              // If it's a single value, just use it as is
              return `${key}=${params[key]}`
            }
          })
          .join('&')
      : ''

    return `${endpoint}?${sortedParams}`
  }

  /**
   * Get value from cache if it exists and is not expired
   *
   * @param {string} key Cache key
   * @returns {any|null} Cached value or null if not found/expired
   */
  get(key) {
    const entry = this.cache[key]

    // Return null if entry doesn't exist
    if (!entry) return null

    // Check if entry has expired
    if (entry.expiry && entry.expiry < Date.now()) {
      this.remove(key)
      return null
    }

    // Update access order for LRU implementation
    this.updateAccessOrder(key)

    return entry.value
  }

  /**
   * Store value in cache
   *
   * @param {string} key Cache key
   * @param {any} value Value to cache
   * @param {number} ttl Time-to-live in milliseconds (optional)
   */
  set(key, value, ttl) {
    // Enforce cache size limit using LRU eviction policy
    this.enforceSizeLimit()

    // Calculate expiry time if TTL provided
    const expiry =
      ttl || this.defaultTTL ? Date.now() + (ttl || this.defaultTTL) : null

    // Store entry
    this.cache[key] = { value, expiry }

    // Update access order
    this.updateAccessOrder(key)

    return value
  }

  /**
   * Remove entry from cache
   *
   * @param {string} key Cache key
   */
  remove(key) {
    if (this.cache[key]) {
      delete this.cache[key]

      // Update access order
      const index = this.accessOrder.indexOf(key)
      if (index !== -1) {
        this.accessOrder.splice(index, 1)
      }
    }
  }

  /**
   * Clear all entries from cache
   */
  clear() {
    this.cache = {}
    this.accessOrder = []
  }

  /**
   * Clear all entries related to a specific endpoint
   *
   * @param {string} endpoint API endpoint to clear cache for
   */
  clearEndpoint(endpoint) {
    const keysToRemove = Object.keys(this.cache).filter((key) =>
      key.startsWith(`${endpoint}?`)
    )

    keysToRemove.forEach((key) => this.remove(key))
  }

  /**
   * Update the access order for LRU implementation
   *
   * @param {string} key Cache key
   */
  updateAccessOrder(key) {
    // Remove key from current position if it exists
    const index = this.accessOrder.indexOf(key)
    if (index !== -1) {
      this.accessOrder.splice(index, 1)
    }

    // Add key to the end (most recently used)
    this.accessOrder.push(key)
  }

  /**
   * Enforce cache size limit using LRU eviction policy
   */
  enforceSizeLimit() {
    while (
      Object.keys(this.cache).length >= this.maxSize &&
      this.accessOrder.length > 0
    ) {
      // Remove least recently used item
      const oldestKey = this.accessOrder.shift()
      if (oldestKey && this.cache[oldestKey]) {
        delete this.cache[oldestKey]
      }
    }
  }

  /**
   * Get API data with caching
   *
   * @param {Function} fetchFunction Async function that returns API data
   * @param {string} endpoint API endpoint
   * @param {Object} params Request parameters
   * @param {Object} options Cache options
   * @returns {Promise<any>} API data
   */
  async getData(fetchFunction, endpoint, params = {}, options = {}) {
    const key = this.generateKey(endpoint, params)
    const cachedData = this.get(key)

    // Return cached data if available
    if (cachedData && !options.bypassCache) {
      return cachedData
    }

    try {
      // Fetch fresh data
      const data = await fetchFunction()

      // Cache the result if caching is not disabled
      if (!options.skipCache) {
        this.set(key, data, options.ttl)
      }

      return data
    } catch (error) {
      // Optionally fall back to stale data on error
      if (options.useStaleCacheOnError) {
        const staleData = this.getCacheEntryIgnoringExpiry(key)
        if (staleData) {
          console.warn('Using stale cache data due to fetch error')
          return staleData
        }
      }

      // Re-throw the error if no stale data or fallback disabled
      throw error
    }
  }

  /**
   * Get cache entry ignoring expiry check
   *
   * @param {string} key Cache key
   * @returns {any|null} Cached value or null if not found
   */
  getCacheEntryIgnoringExpiry(key) {
    const entry = this.cache[key]
    return entry ? entry.value : null
  }
}

// Export singleton instance
const cacheService = new CacheService()
export default cacheService
